// srtapp.js
import React, { useState, useCallback } from "react";
import axios from 'axios';
import { 
    Container, Typography, Button, Box, Paper, Card, CardContent,
    List, ListItem, ListItemIcon, ListItemText, CircularProgress, Alert
} from "@mui/material";
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import MovieIcon from '@mui/icons-material/Movie';

const url_self = process.env.REACT_APP_CURRENT_URL;

export function AppContent() {
    const [file, setFile] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [downloadedFiles, setDownloadedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
            setError(null);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/srt': ['.srt'] },
        maxFiles: 1
    });

    const handleUpload = async () => {
        if (!file) {
            setError("Selecteer eerst een SRT-bestand.");
            return;
        }
        
        setLoading(true);
        setError(null);
        
        const formData = new FormData();
        formData.append("srtFile", file);

        try {
            const response = await axios.post(`${url_self}/convert-srt-to-fcpxml`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setUploadedFiles(prev => [...prev, file.name]);

            // Handle download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const downloadFileName = file.name.replace('.srt', '.fcpxml');
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', downloadFileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setDownloadedFiles(prev => [...prev, downloadFileName]);
            setFile(null); // Reset for next upload
        } catch (error) {
            console.error('Er was een fout bij het uploaden van het bestand:', error);
            setError('Fout bij het converteren van het bestand. Controleer of het een geldig SRT-bestand is.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper 
                elevation={3} 
                sx={{ 
                    p: 4, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)'
                }}
            >
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <MovieIcon sx={{ fontSize: 48, color: '#5c6bc0', mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        SRT → FCP-XML
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Converteer ondertitelingsbestanden naar Final Cut Pro XML
                    </Typography>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Dropzone */}
                <Box
                    {...getRootProps()}
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : file ? 'success.main' : 'grey.400',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragActive ? 'action.hover' : file ? 'success.light' : 'grey.50',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover'
                        },
                        mb: 3
                    }}
                >
                    <input {...getInputProps()} />
                    {file ? (
                        <>
                            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                            <Typography variant="h6" color="success.main">
                                {file.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Klik of sleep om te wijzigen
                            </Typography>
                        </>
                    ) : (
                        <>
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                            <Typography variant="h6" color="text.secondary">
                                {isDragActive ? 'Laat los om te uploaden' : 'Sleep SRT-bestand hierheen'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                of klik om te selecteren
                            </Typography>
                        </>
                    )}
                </Box>

                {/* Convert Button */}
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleUpload}
                    disabled={!file || loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    sx={{ 
                        py: 1.5, 
                        fontSize: '1.1rem',
                        borderRadius: 2,
                        color: '#ffffff !important',
                        background: 'linear-gradient(45deg, #5c6bc0 30%, #3f51b5 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #3949ab 30%, #303f9f 90%)',
                        },
                        '& .MuiButton-startIcon': {
                            color: '#ffffff'
                        }
                    }}
                >
                    {loading ? 'Converteren...' : 'Converteer naar FCP-XML'}
                </Button>

                {/* Success History */}
                {downloadedFiles.length > 0 && (
                    <Card sx={{ mt: 4, backgroundColor: 'success.light', borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CheckCircleIcon color="success" />
                                Geconverteerde bestanden
                            </Typography>
                            <List dense>
                                {downloadedFiles.map((fileName, index) => (
                                    <ListItem key={index} sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <DescriptionIcon color="success" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={fileName} 
                                            primaryTypographyProps={{ fontWeight: 500 }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                )}

                {/* Info */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        💡 FCP-XML bestanden kunnen direct in Final Cut Pro worden geïmporteerd
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
}
